// src/services/azureResearchService.js
// Azure Table Storage Service für das Research-/Q&A-Tool (Mehr-Projekt-fähig).
//
// Tabellen:
//  - researchQnA        (WRITE) : Fragen + Zitate ALLER Research-Projekte, je Projekt
//                                 partitioniert (qna_<projektId> / zitat_<projektId>)
//  - <project.sourceTable> (READ) : extern befüllte Recherche-Quellen des jeweiligen
//                                 Projekts (z.B. Sakralbauten021Research); wird NICHT geschrieben
//
// Historie: Bis 2026-07 lagen die Sakralbauten-Einträge in Sakralbauten021QnA
// (Partitionen "qna"/"zitat"). Das Transfer-Script scripts/migrate-research-projects.mjs
// kopiert sie nach researchQnA unter der Projekt-ID rp_sakralbauten021.
//
// Sicherheits-Hinweis: Der SAS-Token (rwdlacu, gültig bis 2035) ist – wie in allen
// bestehenden Services – hartcodiert und landet im Frontend-Bundle. Mittelfristig sollte
// er rotiert und über einen Backend-Proxy/Env-Variable bezogen werden.

import DOMPurify from "dompurify";
import { ensureTableExists } from "./azureTableUtils";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  qnaTable: "researchQnA",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Zwei Entitätstypen ("kinds") in DERSELBEN Tabelle, unterschieden über die
// PartitionKey. Fragen (qna) und Zitate (zitat) teilen sich Form + Persistenz.
//  - Inhalts-Slots je kind interpretiert: AnswerHtml = Antwort/Zitattext (Rich),
//    Question = Fragetext/Kontext-Notiz (Plain).
// Die PartitionKey trägt zusätzlich die Projekt-ID: qna_<projektId> / zitat_<projektId>.
const KIND_CONFIG = {
  question: { partitionPrefix: "qna", idPrefix: "q" },
  quote: { partitionPrefix: "zitat", idPrefix: "z" },
};

const partitionFor = (kind, projectId) =>
  `${(KIND_CONFIG[kind] || KIND_CONFIG.question).partitionPrefix}_${projectId}`;

// --- HTML-Sanitisierung (gegen XSS) -----------------------------------------
// Wird vor dem Speichern UND vor dem Rendern (dangerouslySetInnerHTML) genutzt.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "span", "strong", "b", "em", "i", "u", "s", "strike",
    "sub", "sup", "ul", "ol", "li", "a", "h1", "h2", "h3",
    "blockquote", "code", "pre", "mark",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "style", "class"],
};

export const sanitizeHtml = (html) => DOMPurify.sanitize(html || "", SANITIZE_CONFIG);

// --- Helpers ----------------------------------------------------------------
const generateId = (kind) =>
  `${(KIND_CONFIG[kind] || KIND_CONFIG.question).idPrefix}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const parseJsonArray = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseLinkedSources = (raw) =>
  parseJsonArray(raw)
    .filter((r) => r && r.rk)
    .map((r) => ({
      kind: r.kind === "file" ? "file" : "table",
      pk: String(r.pk || ""),
      rk: String(r.rk || ""),
      // Optionale Ergänzung zur Verknüpfung (z.B. "S. 41", "3. Abschnitt").
      // Gehört zur VERKNÜPFUNG (Eintrag<->Quelle), nicht zur Quelle selbst.
      note: String(r.note || ""),
    }));

// Querverweise: JSON-Array von Verweisen auf andere Einträge (Fragen ODER Zitate).
// Neues Format: [{kind, id}]. Abwärtskompatibel: ein nackter String (Altdaten) wird
// als Verweis auf eine Frage interpretiert.
const normalizeCrossKind = (kind) => (kind === "quote" ? "quote" : "question");
const parseCrossrefs = (raw) =>
  parseJsonArray(raw)
    .map((entry) =>
      typeof entry === "string"
        ? { kind: "question", id: entry }
        : { kind: normalizeCrossKind(entry?.kind), id: String(entry?.id || "") }
    )
    .filter((r) => r.id);

// --- Daten-Tabelle (CSV) + Diagramm ----------------------------------------
// Eine Table-Property fasst max. ~32K Zeichen. Grössere Tabellen-JSONs werden
// über mehrere Properties (DataTable0, DataTable1, …) verteilt; DataTableParts
// hält die Anzahl. PUT ersetzt die Entität komplett -> alte Chunks verschwinden.
const DATATABLE_CHUNK_SIZE = 30000;
export const MAX_DATATABLE_CHARS = 150 * 1024; // ~150 KB Guard (1-MB-Entitätslimit)
const CHART_TYPES = ["pie", "bar", "barh", "stacked"];

// Prüft, ob eine Tabelle (als JSON) das Inline-Limit sprengt.
export const dataTableTooLarge = (table) =>
  !!table && JSON.stringify({ columns: table.columns || [], rows: table.rows || [] }).length > MAX_DATATABLE_CHARS;

const parseDataTable = (item) => {
  const parts = Number(item.DataTableParts) || 0;
  if (parts <= 0) return null;
  let json = "";
  for (let i = 0; i < parts; i += 1) json += item[`DataTable${i}`] || "";
  try {
    const parsed = JSON.parse(json);
    if (!parsed || !Array.isArray(parsed.columns)) return null;
    return {
      columns: parsed.columns.map(String),
      rows: Array.isArray(parsed.rows) ? parsed.rows.map((r) => (r || []).map(String)) : [],
    };
  } catch {
    return null;
  }
};

// Tabelle -> Property-Map (DataTableParts + DataTable0..N-1).
const serializeDataTable = (table) => {
  if (!table || !Array.isArray(table.columns) || table.columns.length === 0) {
    return { DataTableParts: "0" };
  }
  const json = JSON.stringify({ columns: table.columns, rows: table.rows || [] });
  const out = {};
  let n = 0;
  for (let i = 0; i < json.length; i += DATATABLE_CHUNK_SIZE) {
    out[`DataTable${n}`] = json.slice(i, i + DATATABLE_CHUNK_SIZE);
    n += 1;
  }
  out.DataTableParts = String(n);
  return out;
};

const safeParseObject = (raw) => {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
};

const parseChart = (raw) => {
  const c = safeParseObject(raw);
  if (!c) return null;
  return {
    type: CHART_TYPES.includes(c.type) ? c.type : "none",
    labelCol: Number.isInteger(c.labelCol) ? c.labelCol : 0,
    valueCols: Array.isArray(c.valueCols) ? c.valueCols.filter((x) => Number.isInteger(x)) : [],
    // Optionale Farb-Überschreibungen je Serie (Balken) bzw. Segment (Kuchen).
    colors: Array.isArray(c.colors) ? c.colors.map((x) => String(x || "")) : [],
  };
};

// Tags werden als ";"-getrennter String gespeichert (App-Konvention) und als Array geliefert.
const parseTagString = (raw) =>
  String(raw || "")
    .split(";")
    .map((t) => t.trim())
    .filter(Boolean);

// Azure-Eintrag -> Domänenobjekt. `kind` ("question" | "quote") bestimmt nur die
// spätere Interpretation der Inhalts-Slots; die Felder sind für beide identisch.
// Die Projekt-Zugehörigkeit steckt in der PartitionKey und wird hier nicht gemappt.
const mapEntity = (item, kind = "question") => ({
  id: item.RowKey,
  kind,
  // ETag für optimistisches Sperren (kommt aus odata=minimalmetadata bzw. Point-Read).
  etag: item["odata.etag"] || "",
  question: item.Question || "",
  answerHtml: item.AnswerHtml || "",
  tags: parseTagString(item.Tags),
  ideas: parseJsonArray(item.Ideas).map((i) => ({
    id: String(i.id || ""),
    text: String(i.text || ""),
    createdBy: String(i.createdBy || ""),
    createdAt: String(i.createdAt || ""),
  })),
  // Fussnoten: gleiches Prinzip/Format wie Ideen (einzeln erfasste Texte).
  footnotes: parseJsonArray(item.Footnotes).map((i) => ({
    id: String(i.id || ""),
    text: String(i.text || ""),
    createdBy: String(i.createdBy || ""),
    createdAt: String(i.createdAt || ""),
  })),
  linkedSources: parseLinkedSources(item.LinkedSources),
  // Querverweise auf andere Einträge (Fragen oder Zitate): [{kind, id}].
  crossrefs: parseCrossrefs(item.Crossrefs),
  // Optionale Daten-Tabelle (aus CSV) + Diagramm-Konfiguration.
  dataTable: parseDataTable(item),
  chart: parseChart(item.Chart),
  // Optionaler SQL-Query (formatierter Plain-Text): dokumentiert, wie die Daten
  // der Tabelle zustande gekommen sind (Datenherkunft).
  sqlQuery: item.SqlQuery || "",
  changeLog: parseJsonArray(item.ChangeLog).map((c) => ({
    by: String(c.by || ""),
    at: String(c.at || ""),
    fields: Array.isArray(c.fields) ? c.fields.map(String) : [],
  })),
  sortOrder: item.SortOrder || "",
  // Kapitelzuordnung (Story: Strukturierung); "" = nicht zugeordnet.
  chapterId: item.ChapterId || "",
  // Unterkapitel innerhalb des Kapitels; "" = direkt im Kapitel (ADR 0038).
  subchapterId: item.SubchapterId || "",
  // Folgefrage-Hierarchie: übergeordnete Frage; "" = oberste Ebene.
  parentId: item.ParentId || "",
  // Feste, unveränderliche Nummer der Frage innerhalb des Research-Projekts
  // (Anzeige "ID001"). Wird EINMAL bei der Anlage vergeben und danach nie mehr
  // geändert — auch nicht beim Umsortieren, Umhängen oder Kapitelwechsel.
  // Nummern gelöschter Fragen werden nicht nachbesetzt. "" = Altdaten ohne
  // Nummer (Backfill: scripts/research-backfill-fragen-ids.mjs).
  questionNo: item.QuestionNo || "",
  createdAt: item.CreatedAt || "",
  updatedAt: item.UpdatedAt || "",
  createdBy: item.CreatedBy || "",
});

const mapSource = (item) => ({
  pk: item.PartitionKey,
  rk: item.RowKey,
  title: item.title || item.Title || "",
  content: item.content || item.Content || "",
  url: item.url || item.Url || "",
  favicon: item.favicon || item.Favicon || "",
  thumbnailData: item.thumbnailData || item.ThumbnailData || "",
  blobUrl: item.blobUrl || item.BlobUrl || "",
  tags: item.tags || item.Tags || "",
  thema: item.thema || item.Thema || "",
  type: item.type || item.Type || "",
  // Autor:in / interviewte Person (nur bei manuell erfassten Quellen befüllt).
  author: item.author || item.Author || "",
});

// Generischer, paginierter Tabellen-Load (Continuation-Token-Muster).
const loadAllEntities = async (tableName, filter = null) => {
  let all = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${tableName}?${AZURE_CONFIG.sasToken}`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }

    const response = await fetch(url, {
      // minimalmetadata liefert pro Eintrag `odata.etag` (für optimistisches Sperren) mit.
      headers: { Accept: "application/json;odata=minimalmetadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error:", errorText);
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();
    all = all.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);

  return all;
};

// --- Einträge (researchQnA: Fragen + Zitate je Projekt) ----------------------
// Generischer Persistenz-Kern, über `kind` ("question" | "quote") und die
// Projekt-ID parametrisiert. IDs sind dank Präfix (q_/z_) global eindeutig.

// Alle Einträge eines kinds laden, sortiert nach SortOrder (numerisch), dann CreatedAt.
const getAllOfKind = async (kind, projectId) => {
  try {
    const items = await loadAllEntities(
      AZURE_CONFIG.qnaTable,
      `PartitionKey eq '${partitionFor(kind, projectId)}'`
    );
    return items
      .map((it) => mapEntity(it, kind))
      .sort((a, b) => {
        const sa = Number(a.sortOrder) || 0;
        const sb = Number(b.sortOrder) || 0;
        if (sa !== sb) return sa - sb;
        return (a.createdAt || "").localeCompare(b.createdAt || "");
      });
  } catch (error) {
    // Tabelle existiert evtl. noch nicht -> leere Liste statt Fehler.
    console.error(`Load ${kind} Error:`, error);
    return [];
  }
};

const entityUrl = (kind, projectId, rowKey) =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.qnaTable}(PartitionKey='${partitionFor(kind, projectId)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

// Konditionaler Schreibvorgang.
//  - ohne `ifMatch`: Insert-or-Replace (Upsert) — für neue Einträge.
//  - mit `ifMatch` (ETag): Replace nur, wenn der ETag noch passt; sonst HTTP 412.
// Liefert den neuen ETag zurück (aus dem Response-Header oder – falls per CORS
// nicht lesbar – via Point-Read als Fallback).
const writeEntity = async (kind, projectId, entity, ifMatch) => {
  await ensureTableExists(AZURE_CONFIG.qnaTable);
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json;odata=nometadata",
  };
  if (ifMatch) headers["If-Match"] = ifMatch;
  const response = await fetch(entityUrl(kind, projectId, entity.RowKey), {
    method: "PUT",
    headers,
    body: JSON.stringify(entity),
  });
  if (response.status === 412) {
    // Optimistischer Sperr-Konflikt: jemand anderes hat den Eintrag zwischenzeitlich geändert.
    const err = new Error("ETag-Konflikt");
    err.status = 412;
    throw err;
  }
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Azure Error Details:", errorText);
    throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
  }
  const headerEtag = response.headers.get("ETag");
  if (headerEtag) return headerEtag;
  // Fallback: ETag-Header nicht lesbar -> aktuellen Stand laden.
  const fresh = await readEntity(kind, projectId, entity.RowKey);
  return fresh?.etag || "";
};

// Einzelnen Eintrag frisch laden (Point-Read) inkl. aktuellem ETag.
const readEntity = async (kind, projectId, id) => {
  const response = await fetch(entityUrl(kind, projectId, id), {
    headers: { Accept: "application/json;odata=minimalmetadata" },
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure Read Error: ${response.status} - ${errorText}`);
  }
  return mapEntity(await response.json(), kind);
};

// Domänenobjekt -> Azure-Entität. HTML wird sanitisiert.
const buildEntity = (e, kind, projectId, nowIso) => ({
  PartitionKey: partitionFor(kind, projectId),
  RowKey: e.id,
  Question: String(e.question || ""),
  AnswerHtml: sanitizeHtml(e.answerHtml || ""),
  Tags: (e.tags || [])
    .map((t) => String(t).trim())
    .filter(Boolean)
    .join(";"),
  Ideas: JSON.stringify(
    (e.ideas || []).map((i) => ({
      id: String(i.id || ""),
      text: String(i.text || ""),
      createdBy: String(i.createdBy || ""),
      createdAt: String(i.createdAt || ""),
    }))
  ),
  Footnotes: JSON.stringify(
    (e.footnotes || []).map((i) => ({
      id: String(i.id || ""),
      text: String(i.text || ""),
      createdBy: String(i.createdBy || ""),
      createdAt: String(i.createdAt || ""),
    }))
  ),
  LinkedSources: JSON.stringify(
    (e.linkedSources || []).map((r) => ({
      kind: r.kind === "file" ? "file" : "table",
      pk: r.pk || "",
      rk: r.rk,
      note: String(r.note || ""),
    }))
  ),
  // Querverweise auf andere Einträge (Fragen/Zitate), dedupliziert per kind:id.
  Crossrefs: JSON.stringify(dedupeCrossrefs(e.crossrefs)),
  // Daten-Tabelle (gechunkt über mehrere Properties) + Diagramm-Konfiguration.
  ...serializeDataTable(e.dataTable),
  Chart: e.chart && e.chart.type && e.chart.type !== "none" ? JSON.stringify(e.chart) : "",
  // SQL-Query als einzelne Property (Plain-Text; Azure-Limit ~32K Zeichen —
  // die UI begrenzt auf 30000). Frei-Text -> im Merge last-write-wins.
  SqlQuery: String(e.sqlQuery || ""),
  // Nur die letzten 3 Änderungen (jüngste zuerst) — für ein künftiges Dashboard.
  ChangeLog: JSON.stringify((e.changeLog || []).slice(0, 3)),
  SortOrder: String(e.sortOrder || Date.now()),
  // Kapitelzuordnung (leer = nicht zugeordnet).
  ChapterId: String(e.chapterId || ""),
  // Unterkapitel (leer = direkt im Kapitel).
  SubchapterId: String(e.subchapterId || ""),
  // Folgefrage-Hierarchie (leer = oberste Ebene).
  ParentId: String(e.parentId || ""),
  // Feste Fragen-Nummer. MUSS hier stehen: writeEntity nutzt PUT (Replace) —
  // ein fehlendes Feld würde die Nummer bei jedem Speichern entfernen.
  QuestionNo: String(e.questionNo || ""),
  CreatedAt: e.createdAt || nowIso,
  UpdatedAt: nowIso,
  CreatedBy: String(e.createdBy || ""),
});

// Querverweise normalisieren + per kind:id deduplizieren.
const dedupeCrossrefs = (refs) => {
  const byKey = new Map();
  (refs || []).forEach((r) => {
    const id = String(r?.id || r || "");
    if (!id) return;
    const kind = typeof r === "string" ? "question" : normalizeCrossKind(r?.kind);
    byKey.set(`${kind}:${id}`, { kind, id });
  });
  return Array.from(byKey.values());
};

// Zwei Versionen desselben Eintrags zusammenführen (Konfliktauflösung bei 412).
// Grundsatz: additive Felder werden VEREINT, damit keine fremden Beiträge verloren
// gehen — insbesondere Ideen (Kern des Lost-Update-Bugfixes). Frei-Text (Frage/
// Antwort bzw. Zitat/Kontext) lässt sich nicht mergen -> lokaler Stand gewinnt.
const mergeEntities = (remote, local) => {
  // Ideen per id vereinen; bei gleicher id gewinnt die lokale Fassung (z.B. Edit).
  const ideaById = new Map();
  (remote.ideas || []).forEach((i) => ideaById.set(i.id, i));
  (local.ideas || []).forEach((i) => ideaById.set(i.id, i));

  // Fussnoten per id vereinen (gleiches Prinzip wie Ideen).
  const footnoteById = new Map();
  (remote.footnotes || []).forEach((i) => footnoteById.set(i.id, i));
  (local.footnotes || []).forEach((i) => footnoteById.set(i.id, i));

  // Verknüpfte Quellen per rk vereinen (inkl. Ergänzungs-Note: lokal gewinnt).
  const srcByRk = new Map();
  (remote.linkedSources || []).forEach((r) => srcByRk.set(r.rk, r));
  (local.linkedSources || []).forEach((r) => srcByRk.set(r.rk, r));

  // Querverweise (kind:id) vereinen.
  const crossrefs = dedupeCrossrefs([
    ...(remote.crossrefs || []),
    ...(local.crossrefs || []),
  ]);

  // Tags case-insensitiv vereinen (erste Schreibweise gewinnt).
  const tagByKey = new Map();
  [...(remote.tags || []), ...(local.tags || [])].forEach((t) => {
    const k = String(t).toLowerCase();
    if (!tagByKey.has(k)) tagByKey.set(k, t);
  });

  // Änderungs-Verlauf vereinen und auf die jüngsten 3 kürzen.
  const logKey = (c) => `${c.at}|${c.by}|${(c.fields || []).join(",")}`;
  const logByKey = new Map();
  [...(local.changeLog || []), ...(remote.changeLog || [])].forEach((c) =>
    logByKey.set(logKey(c), c)
  );
  const changeLog = Array.from(logByKey.values())
    .sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")))
    .slice(0, 3);

  return {
    ...local,
    ideas: Array.from(ideaById.values()),
    footnotes: Array.from(footnoteById.values()),
    linkedSources: Array.from(srcByRk.values()),
    tags: Array.from(tagByKey.values()),
    crossrefs,
    changeLog,
    createdAt: remote.createdAt || local.createdAt,
    // Unveränderlich: ein bereits vergebener Remote-Wert gewinnt immer, damit
    // ein Konflikt-Merge die Nummer nie überschreibt oder verliert.
    questionNo: remote.questionNo || local.questionNo || "",
    etag: remote.etag,
  };
};

// Neuen (leeren) Eintrag des kinds anlegen. `parentId`/`sortOrder` sind optional
// und werden für direkt angelegte Folgefragen gesetzt (US-RESE-0xx): die neue
// Frage hängt dann sofort an der Mutterfrage, ohne zweiten Schreibvorgang.
const createOfKind = async (
  kind,
  projectId,
  { question = "", createdBy = "", parentId = "", sortOrder, questionNo = "" } = {}
) => {
  const id = generateId(kind);
  const now = new Date().toISOString();
  const entity = buildEntity(
    { id, question, createdBy, parentId, sortOrder: sortOrder || Date.now(), questionNo },
    kind,
    projectId,
    now
  );
  // Neuer Eintrag: bewusst ohne If-Match -> Insert-or-Replace.
  const etag = await writeEntity(kind, projectId, entity);
  return { ...mapEntity(entity, kind), etag };
};

// Bestehenden Eintrag speichern – mit optimistischem Sperren (ETag) und Merge-Retry.
// Bei einem Konflikt (412) wird der aktuelle Stand geladen, mit dem lokalen Stand
// zusammengeführt (Ideen/Quellen/Tags/Querverweise additiv) und erneut geschrieben.
const saveOfKind = async (kind, projectId, e) => {
  if (!e || !e.id) throw new Error("saveEntity: id fehlt");
  const now = new Date().toISOString();
  let desired = e;
  let ifMatch = e.etag || "";

  // Schutz der unveränderlichen Fragen-Nummer. writeEntity schreibt per PUT
  // (Replace): ein Client, der die Frage geladen hat, BEVOR sie eine Nummer
  // bekam, hält lokal `questionNo === ""` und würde die gespeicherte Nummer
  // beim nächsten Speichern löschen — auch mit aktuellem Code, allein wegen
  // veralteter Browser-Daten (am 2026-08-24 real passiert: 277 Nummern weg).
  // Fehlt die Nummer lokal, wird sie deshalb frisch nachgeladen statt geleert.
  // Eine Nummer wird auf diesem Weg NIE überschrieben, nur wiederhergestellt.
  if (kind === "question" && !desired.questionNo) {
    try {
      const remote = await readEntity(kind, projectId, e.id);
      if (remote && remote.questionNo) {
        desired = { ...desired, questionNo: remote.questionNo };
        if (!ifMatch) ifMatch = remote.etag || "";
      }
    } catch {
      /* Point-Read fehlgeschlagen -> normal weiterspeichern (Nummer nachtragbar) */
    }
  }

  for (let attempt = 0; attempt < 6; attempt += 1) {
    // Ohne bekannten ETag erst den aktuellen Stand holen, statt fremde Daten blind
    // zu überschreiben (Schutz auch bei Altdaten ohne ETag).
    if (!ifMatch) {
      const remote = await readEntity(kind, projectId, e.id);
      if (remote) {
        desired = mergeEntities(remote, desired);
        ifMatch = remote.etag || "";
      }
      // remote === null -> Eintrag existiert (noch) nicht -> unkonditionaler Insert.
    }

    const entity = buildEntity(desired, kind, projectId, now);
    try {
      const etag = await writeEntity(kind, projectId, entity, ifMatch || undefined);
      return { ...mapEntity(entity, kind), etag };
    } catch (err) {
      if (err.status !== 412) throw err;
      const remote = await readEntity(kind, projectId, e.id);
      if (!remote) {
        ifMatch = ""; // wurde gelöscht -> als Insert neu anlegen
        continue;
      }
      desired = mergeEntities(remote, desired);
      ifMatch = remote.etag || "";
    }
  }
  throw new Error("saveEntity: Schreibkonflikt konnte nicht aufgelöst werden");
};

const deleteOfKind = async (kind, projectId, id) => {
  const response = await fetch(entityUrl(kind, projectId, id), {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });
  if (!response.ok && response.status !== 404) {
    const errorText = await response.text();
    throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
  }
  return true;
};

// --- Kind-spezifische, öffentliche Wrapper (je Research-Projekt) -------------
export const getAllQuestions = (projectId) => getAllOfKind("question", projectId);
export const createQuestion = (projectId, args) => createOfKind("question", projectId, args);

// Nächste freie Fragen-Nummer eines Research-Projekts (höchste vergebene + 1).
// Bewusst FRISCH aus der Tabelle statt aus der geladenen Liste: die kann Stunden
// alt sein, und eine doppelt vergebene "unveränderliche" Nummer liesse sich
// nachträglich nicht sauber reparieren. Nummern gelöschter Fragen werden NICHT
// nachbesetzt (Maximum, nicht Anzahl). Fällt die Abfrage aus, liefert sie 0 —
// die Frage entsteht dann ohne Nummer statt gar nicht (der Backfill kann sie
// später nachtragen).
// Feste Fragen-Nummer für die Anzeige: "1" -> "ID001". Leer/0 (Altdaten ohne
// Nummer) -> "" — die Anzeige lässt die ID dann einfach weg.
export const formatQuestionNo = (no) => {
  const n = Number(no) || 0;
  return n > 0 ? `ID${String(n).padStart(3, "0")}` : "";
};

export const getNextQuestionNo = async (projectId) => {
  if (!projectId) return 0;
  try {
    const items = await loadAllEntities(
      AZURE_CONFIG.qnaTable,
      `PartitionKey eq '${partitionFor("question", projectId)}'`
    );
    const max = items.reduce((m, it) => Math.max(m, Number(it.QuestionNo) || 0), 0);
    return max + 1;
  } catch (error) {
    console.error("getNextQuestionNo Error:", error);
    return 0;
  }
};
export const saveQuestion = (projectId, q) => saveOfKind("question", projectId, q);
export const deleteQuestion = (projectId, id) => deleteOfKind("question", projectId, id);

export const getAllQuotes = (projectId) => getAllOfKind("quote", projectId);
export const createQuote = (projectId, args) => createOfKind("quote", projectId, args);
export const saveQuote = (projectId, z) => saveOfKind("quote", projectId, z);
export const deleteQuote = (projectId, id) => deleteOfKind("quote", projectId, id);

// --- Kapitel (Story: Strukturierung) — je Projekt, in derselben researchQnA-Tabelle
// unter der Partition chapter_<projektId>. Nur Name + Reihenfolge; Fragen verweisen
// per ChapterId. Kapitel sind bewusst schlank (kein Rich-Content).
// Unterkapitel (ADR 0038) leben in DERSELBEN Partition: RowKey-Präfix `sc_` und
// Property ParentChapterId (RowKey des Kapitels). Fragen verweisen per SubchapterId.
const chapterPartition = (projectId) => `chapter_${projectId}`;
const chapterCollectionUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.qnaTable}?${AZURE_CONFIG.sasToken}`;
const chapterEntityUrl = (projectId, rowKey) =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.qnaTable}(PartitionKey='${chapterPartition(projectId)}',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

export const getAllChapters = async (projectId) => {
  if (!projectId) return [];
  try {
    const items = await loadAllEntities(
      AZURE_CONFIG.qnaTable,
      `PartitionKey eq '${chapterPartition(projectId)}'`
    );
    return items
      .map((it) => ({
        id: it.RowKey,
        name: it.Name || "",
        sortOrder: Number(it.SortOrder) || 0,
        // "" = Kapitel (oberste Ebene); sonst RowKey des übergeordneten Kapitels.
        parentChapterId: it.ParentChapterId || "",
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Load chapters Error:", error);
    return [];
  }
};

// Kapitel (ohne parentChapterId) oder Unterkapitel (mit) anlegen.
export const createChapter = async (projectId, name, sortOrder, parentChapterId = "") => {
  await ensureTableExists(AZURE_CONFIG.qnaTable);
  const prefix = parentChapterId ? "sc" : "c";
  const id = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const order = Number.isFinite(sortOrder) ? sortOrder : Date.now();
  const entity = {
    PartitionKey: chapterPartition(projectId),
    RowKey: id,
    Name: String(name || "").trim(),
    SortOrder: String(order),
    ParentChapterId: String(parentChapterId || ""),
    CreatedAt: new Date().toISOString(),
  };
  const response = await fetch(chapterCollectionUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const t = await response.text();
    throw new Error(`Kapitel anlegen fehlgeschlagen: ${response.status} - ${t}`);
  }
  return { id, name: entity.Name, sortOrder: order, parentChapterId: entity.ParentChapterId };
};

// Teil-Update (MERGE) eines Kapitels: nur die übergebenen Felder werden gesetzt.
const mergeChapter = async (projectId, id, props) => {
  const response = await fetch(chapterEntityUrl(projectId, id), {
    method: "MERGE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
      "If-Match": "*",
    },
    body: JSON.stringify({
      PartitionKey: chapterPartition(projectId),
      RowKey: id,
      ...props,
    }),
  });
  if (!response.ok) {
    const t = await response.text();
    throw new Error(`Kapitel aktualisieren fehlgeschlagen: ${response.status} - ${t}`);
  }
  return true;
};

export const renameChapter = (projectId, id, name) =>
  mergeChapter(projectId, id, { Name: String(name || "").trim() });

// Reihenfolge setzen: SortOrder entspricht dem Index in orderedIds.
export const reorderChapters = async (projectId, orderedIds) => {
  await Promise.all(
    (orderedIds || []).map((id, i) =>
      mergeChapter(projectId, id, { SortOrder: String(i) })
    )
  );
  return true;
};

export const deleteChapter = async (projectId, id) => {
  const response = await fetch(chapterEntityUrl(projectId, id), {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });
  if (!response.ok && response.status !== 404) {
    const t = await response.text();
    throw new Error(`Kapitel löschen fehlgeschlagen: ${response.status} - ${t}`);
  }
  return true;
};

// --- Quellen (extern befüllte Tabelle des Projekts) — nur lesen ---------------

// Gemeinsame Quellen-Tabelle für neue Projekte: EINE Tabelle für alle Projekte,
// PartitionKey = Projekt-ID (gleiches Muster wie researchQnA). Wird von der App
// bei Bedarf selbst angelegt; Plugins schreiben nur noch hinein.
// Alt-Projekte (z.B. Sakralbauten -> Sakralbauten021Research) behalten ihre
// eigene Tabelle, die weiterhin über ALLE Partitionen gelesen wird.
export const SHARED_SOURCES_TABLE = "researchSources";

// Alle Quellen des Projekts laden. Ohne konfigurierte Tabelle: leere Liste.
//  - Gemeinsame Tabelle (researchSources): nur die Partition des Projekts.
//  - Projekteigene/externe Tabelle: alle Partitionen (damit künftige Themen mitkommen).
export const loadAllSources = async (sourceTable, projectId) => {
  if (!sourceTable) return [];
  try {
    if (sourceTable === SHARED_SOURCES_TABLE) {
      if (!projectId) return [];
      await ensureTableExists(SHARED_SOURCES_TABLE);
      const items = await loadAllEntities(sourceTable, `PartitionKey eq '${projectId}'`);
      return items.map(mapSource);
    }
    const items = await loadAllEntities(sourceTable);
    return items.map(mapSource);
  } catch (error) {
    console.error("Load Sources Error:", error);
    return [];
  }
};

// --- Manuell erfasste Quellen (Buch / Zeitschrift / Interview) ----------------
// Erster Schreibpfad der App in die Quellen-Tabelle (bislang nur von Plugins
// befüllt). PartitionKey = Projekt-ID (in der gemeinsamen Tabelle ohnehin die
// Konvention; projekteigene Tabellen werden über alle Partitionen gelesen,
// eine zusätzliche Partition stört dort nicht). RowKey-Präfix `manual_` macht
// die Herkunft erkennbar. Feldnamen lowercase — wie die Plugin-Einträge.
export const MANUAL_SOURCE_TYPES = [
  { value: "buch", label: "Buch" },
  { value: "zeitschrift", label: "Zeitschrift" },
  { value: "interview", label: "Interview" },
];

export const createManualSource = async (sourceTable, projectId, data) => {
  if (!sourceTable || !projectId) {
    throw new Error("Für dieses Projekt ist keine Quellen-Tabelle konfiguriert.");
  }
  const type = MANUAL_SOURCE_TYPES.some((t) => t.value === data?.type) ? data.type : "buch";
  const title = String(data?.title || "").trim();
  if (!title) throw new Error("Titel fehlt.");
  await ensureTableExists(sourceTable);
  const entity = {
    PartitionKey: projectId,
    RowKey: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title,
    author: String(data?.author || "").trim(),
    content: String(data?.content || "").trim(),
    thema: String(data?.thema || "").trim(),
    url: String(data?.url || "").trim(),
    type,
    tags: "",
    createdAt: new Date().toISOString(),
    createdBy: String(data?.createdBy || ""),
  };
  const response = await fetch(
    `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${sourceTable}?${AZURE_CONFIG.sasToken}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    }
  );
  if (!response.ok) {
    const t = await response.text();
    throw new Error(`Quelle anlegen fehlgeschlagen: ${response.status} - ${t}`);
  }
  return mapSource(entity);
};

// Clientseitige Volltextsuche (Azure Table kennt kein contains/LIKE).
// Multi-Wort-AND über title/content/url/tags/thema/author.
export const filterSources = (sources, term) => {
  const t = (term || "").trim().toLowerCase();
  if (!t) return sources;
  const words = t.split(/\s+/).filter(Boolean);
  return sources.filter((s) => {
    const haystack = `${s.title} ${s.content} ${s.url} ${s.tags} ${s.thema} ${s.author || ""}`.toLowerCase();
    return words.every((w) => haystack.includes(w));
  });
};
